import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';
import { Customers } from '../../../account-info-header/models/AccountInfoApiResponse';
import { selectEAuthCustomers, selectHasGoingDigitalFlowInternalTransferCreateSloa, selectIsEstateOrTestTrust, selectIsOrgAccount, selectUsername, selectUseTestTrustDigitalToPdfFlow } from '../../../account-info-header/reducers';
import { selectHasAccessInitiateMoneyMovement } from '../../../authorize/reducers';
import { CodeAndDescription } from '../../../create-request/models/shared/CodeAndDescription';
import { EAuthInfo } from '../../../shared/models/EAuthInfo';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import { selectCaseId, selectEAuthInfo, selectInitiatedBy, selectLoginId, selectRequestedDate, selectRequestedTime, selectSource, selectTransactionStatus } from '../../../shared/reducers';
import { confirmationStatusMessage, pendReasonsStrings, RegTypeCode, sourceOfLastModification, transactionTypes } from '../../../shared/utilities/Constants';
import { selectIsFromTableOrView, selectIsSaPendingAuthorization, selectIsSubmitandCreateBundledSa, selectSaPendReasons, selectStandingAuthCanRelease, selectStandingAuthPendForRelease, selectToAccountRegistrationType, selectTransactionType } from '../../reducers';
import { selectInitReleaseInitiatedBy } from '../../../initiate-and-release/reducers';
import { FormatDatePipe } from '../../../shared/pipes/FormatDatePipe';
import { FormatTimePipe } from '../../../shared/pipes/FormatTimePipe';

@Component({
  standalone: false,
  selector: 'mm-sa-confirmation-details-section',
  templateUrl: './sa-confirmation-details-section.component.html',
  styleUrls: ['./sa-confirmation-details-section.component.scss']
})
export class SaConfirmationDetailsSectionComponent implements OnInit {
  @Input() isInrReleasorFlow: boolean;
  @Input() draftInContext: boolean;
  destroy$ = new Subject<void>();
  caseId: string;
  requestedTime$: Observable<string>;
  requestedDate$: Observable<string>;
  dig2PdfDateTime: string;
  source$: Observable<string | undefined>;
  showCaseId: boolean = false;
  transactionType: transactionTypes;
  selectedEauthCustomers: Customers[] = [];
  pendReasons: string[];
  status: string;
  username: string;
  namePipe = new NamePipe();
  isPendingAuthorization: boolean;
  isFromEdit: boolean;
  readonly pendReasonStrings = pendReasonsStrings;
  readonly dig2PdfSource = sourceOfLastModification.SchwabAdvisorCenter
  initiatedBy: string;
  loginId: string;
  hasReleaseAccess: boolean;
  hasInitiateAccess: boolean;
  isPendForRelease: boolean;
  isBsloa: boolean;
  accountInfo: any;
  useTestTrustOrEstateDigitalToPdfFlow: boolean;
  useTestTrustDigitalToPdfFlow: boolean;
  isITi401kToAccount: boolean = false;

  constructor(private store: Store) { }

  ngOnInit(): void {
    this.store.select(selectCaseId).pipe(takeUntil(this.destroy$)).subscribe(caseId => {
      if (caseId) {
        this.caseId = caseId;
        this.showCaseId = true;
      }
    });
    this.requestedTime$ = this.store.select(selectRequestedTime);
    this.requestedDate$ = this.store.select(selectRequestedDate);
    this.source$ = this.store.select(selectSource).pipe(
      takeUntil(this.destroy$),
      map(source => this.getSource(source))
    );

    combineLatest([
      this.store.select(selectInitiatedBy),
      this.store.select(selectInitReleaseInitiatedBy)
    ]).pipe(takeUntil(this.destroy$))
    .subscribe(([initiatedBy, inRInitiatedBy]) => {
      this.initiatedBy = this.isInrReleasorFlow && inRInitiatedBy ? inRInitiatedBy : initiatedBy ?? ''
    });

    combineLatest([
      this.store.select(selectUseTestTrustDigitalToPdfFlow),
      this.store.select(selectHasGoingDigitalFlowInternalTransferCreateSloa),
      this.store.select(selectTransactionType),
      this.store.select(selectToAccountRegistrationType)
    ])
      .pipe(takeUntil(this.destroy$)).subscribe(([useTestTrustDigitalToPdfFlow, hasGoingDigitalFlowInternalTransferCreateSloa, transactionType, toAccountRegType]) => {
        this.isITi401kToAccount = (toAccountRegType === RegTypeCode.INDIVIDUAL_401K || toAccountRegType === RegTypeCode.ROTH_401K) && transactionType === transactionTypes.internalTransfer;
        if (transactionType === transactionTypes.check) {
          this.useTestTrustDigitalToPdfFlow = useTestTrustDigitalToPdfFlow;
        }
        if (transactionType === transactionTypes.internalTransfer) {
          this.useTestTrustDigitalToPdfFlow = useTestTrustDigitalToPdfFlow && !hasGoingDigitalFlowInternalTransferCreateSloa;
        }
        if (this.useTestTrustDigitalToPdfFlow || this.isITi401kToAccount) {
          this.dig2PdfDateTime = this.getRequestedDateTime();
        }
      });

    this.store.select(selectIsSubmitandCreateBundledSa).pipe(takeUntil(this.destroy$)).subscribe(isBsloa => this.isBsloa = isBsloa);

    this.store.select(selectLoginId).pipe(takeUntil(this.destroy$)).subscribe(val => this.loginId = val ?? '');

    this.store.select(selectIsSaPendingAuthorization).pipe(takeUntil(this.destroy$)).subscribe(isPendingAuthorization => this.isPendingAuthorization = isPendingAuthorization);
    this.store.select(selectSaPendReasons).pipe(
      takeUntil(this.destroy$)
    ).subscribe(pendReasons => {
      this.pendReasons = pendReasons;
    });
    this.store.select(selectUsername).pipe(takeUntil(this.destroy$)).subscribe(username => this.username = username);
    this.store.select(selectIsFromTableOrView).pipe(takeUntil(this.destroy$)).subscribe((isFromTableOrView) => {
      this.isFromEdit = isFromTableOrView;
    });

    combineLatest([
      this.store.select(selectTransactionStatus),
      this.store.select(selectEAuthInfo),
      this.store.select(selectEAuthCustomers),
      this.store.select(selectIsOrgAccount),
      this.store.select(selectHasAccessInitiateMoneyMovement),
      this.store.select(selectStandingAuthPendForRelease),
      this.store.select(selectStandingAuthCanRelease)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([transactionStatus, eAuthInfo, eAuthCustomers, isOrgAccount, hasInitiate, pendForRelease, canRelease]) => {
        this.selectedEauthCustomers = [];
        if (isOrgAccount) {
          this.selectedEauthCustomers = eAuthInfo.isAccountHolderSelected ? eAuthCustomers.filter(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]) : [];
        } else {
          eAuthInfo.selectedAccountHolder.forEach(holder => {
            let selectedCustomer = eAuthCustomers.find(customer => customer.customerId === holder);
            if (selectedCustomer) {
              this.selectedEauthCustomers.push(selectedCustomer);
            }
          });
        }
        this.isPendForRelease = pendForRelease;
        this.hasInitiateAccess = hasInitiate;
        this.hasReleaseAccess = canRelease;
        this.status = this.statusMessage(transactionStatus, !eAuthInfo.isOptedOut, this.selectedEauthCustomers, eAuthInfo);
    });
  }

  getInitiatedBy(): string {
    return this.initiatedBy ? this.initiatedBy : this.username;
  }

  statusMessage(transactionStatus: CodeAndDescription, isEAuth: boolean = false, customerList: Customers[] | undefined, eAuthInfo: EAuthInfo | undefined): string {
    if (this.isITi401kToAccount) {
      return confirmationStatusMessage.onetimeTotalDistribution;
    }
    if (this.useTestTrustOrEstateDigitalToPdfFlow) {
      return 'Awaiting client authorization';
    }
    if (!(transactionStatus.code === 'PEND' ||
        transactionStatus.description === 'Pending') &&
        !(eAuthInfo ? eAuthInfo.isAccountHolderSelected: false) &&
        !(eAuthInfo ? eAuthInfo.isOptedOut : false)) {
      return transactionStatus.description;
    }
    if (this.isPendingAuthorization && isEAuth && customerList) {
      let status : string;
      if (this.hasInitiateAccess && this.isPendForRelease) {
        status = `Initiated - Awaiting authorization from`;
      }
      else {
       status = `${transactionStatus.description} - Awaiting authorization from`;
      }
      let count = 1;
      let length = customerList.length;
      customerList.forEach(customer => {
        status += ` ${this.namePipe.transform(customer?.firstName)} ${this.namePipe.transform(customer?.lastName)} at ${customer.emailId}`
        if (count < length && length != 2) {
          status += `,`;
        }
        if (count === length - 1) {
          status += ` and`;
        }
        count++;
      });
      return status;

    }
    else if (this.isPendingAuthorization && this.hasInitiateAccess && !!eAuthInfo?.isOptedOut && !this.isBsloa) {
      if (this.isPendForRelease) {
        return `Initiated - Awaiting signed authorization`;
      }
      else if (this.hasReleaseAccess) {
        return `${transactionStatus.description} - Awaiting signed authorization`
      }
    }
    else if (this.isPendingAuthorization &&
            (this.pendReasons.includes(this.pendReasonStrings.clientAuthLOA) ||
            this.pendReasons.includes(this.pendReasonStrings.ndfaLPFA) ||
            this.pendReasons.includes(this.pendReasonStrings.taxElections))) {
      return `${transactionStatus.description} - Awaiting signed authorization`
    }
    else if (this.hasInitiateAccess && this.isPendForRelease) {
      return `Initiated - Request received. Schwab is reviewing.`
    }
    return `${transactionStatus.description} - Request received. Schwab is reviewing.`
  }

  get lastModifiedBy(): string {
    return this.username;
  }

  getSource(source: string): string {
    if (source.toLowerCase() === sourceOfLastModification.Schwabinstitutional.toLowerCase()) {
      return sourceOfLastModification.SchwabAdvisorCenter;
    }
    return source ?? '';
  }

  getRequestedDateTime(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    };
    const dateTimeString = now.toLocaleString('en-US', options);
    const [date, time] = dateTimeString.split(', ');
    const formattedDate = new FormatDatePipe().transform(date);
    const formattedTime = new FormatTimePipe().transform(time);
    return `${formattedDate} ${formattedTime} ET`;
  }
}
